/**
 * AiSettingsStep — create-wizard step 9 (W12 Part B, Task B6).
 *
 * Props-in / JSX-out. One field per row (owner ruling 2026-09-10): the model,
 * the block generation mode, and a text button that SWAPS the Advanced view
 * into the step. The token budget lives only there — it is a rescue control
 * («a generation was cut short»), not a decision every creation needs.
 *
 * The Advanced view REPLACES the step body rather than expanding under it: no
 * collapsible sections in create mode, and never a second dialog (the same
 * swap pattern as the library example details, designer invariant 18).
 *
 * Mock: `.design/create-strategy-wizard/` canvas v3.3, artboards Step9 /
 * Step9Advanced.
 */
import React, { useState } from 'react';
import { Box, Button, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AvailableModelSelector } from '@modules/ai-settings';
import type { StrategyBlockGenerationMode } from '@modules/shared-types';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import { BlockGenerationModeSelector } from '../../BlockGenerationModeSelector';
import type { ModelPricing } from '../generationEstimate';

/** Bounds of the token budget — the model's own ceiling, not a product choice. */
export const MAX_OUTPUT_TOKEN_BOUNDS = { min: 1000, max: 100000, step: 1000 } as const;

/** The default, spelled the way the captions show it. */
const DEFAULT_MAX_TOKENS_LABEL = '100 000';

/**
 * One keystroke of the token field → a number the request can carry.
 * Empty, `0` and anything unparseable fall back to the default (the ceiling);
 * above the ceiling is capped immediately. A value BELOW the floor is let
 * through here — otherwise typing «32000» would be rewritten to «1000» at the
 * first digit — and is lifted by `clampMaxOutputTokens` when the field commits.
 */
export const sanitizeMaxOutputTokens = (raw: string): number => {
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return MAX_OUTPUT_TOKEN_BOUNDS.max;
  return Math.min(parsed, MAX_OUTPUT_TOKEN_BOUNDS.max);
};

/** The committed value: always inside the bounds the field advertises. */
export const clampMaxOutputTokens = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) return MAX_OUTPUT_TOKEN_BOUNDS.max;
  return Math.min(Math.max(value, MAX_OUTPUT_TOKEN_BOUNDS.min), MAX_OUTPUT_TOKEN_BOUNDS.max);
};

export interface AiSettingsStepProps {
  projectId: string;
  model: string;
  onModelChange: (model: string) => void;
  /** Prices of the SELECTED model, resolved by the dialog (`resolveModelPricing`). */
  pricing: ModelPricing;
  blockGenerationMode: StrategyBlockGenerationMode;
  onBlockGenerationModeChange: (mode: StrategyBlockGenerationMode) => void;
  maxTokens: number;
  onMaxTokensChange: (value: number) => void;
  /**
   * W13 IS2 (ruling R1, AC-W13-24): print the actors' own words under each
   * thesis (`insightSelection.includeSources`). OFF by default — quotes make
   * the prompt bigger, and nothing about today's output depends on them.
   */
  includeInsightSources: boolean;
  onIncludeInsightSourcesChange: (value: boolean) => void;
  disabled?: boolean;
  /** The model catalog is still loading (the selector shows its own state). */
  modelLoading?: boolean;
}

const priceLine = (pricing: ModelPricing): string => {
  const base = 'Prices per 1M tokens · input / output';
  const { inputUsdPerMTokens: input, outputUsdPerMTokens: output } = pricing;
  if (input === undefined || output === undefined) return base;
  // Same money formatting as the review step's cost row — «$0.75», never «$10».
  return `${base} · $${input.toFixed(2)} / $${output.toFixed(2)}`;
};

export const AiSettingsStep: React.FC<AiSettingsStepProps> = ({
  projectId,
  model,
  onModelChange,
  pricing,
  blockGenerationMode,
  onBlockGenerationModeChange,
  maxTokens,
  onMaxTokensChange,
  includeInsightSources,
  onIncludeInsightSourcesChange,
  disabled = false,
  modelLoading = false,
}) => {
  /**
   * Which of the two views the step shows. Purely presentational — the VALUE
   * lives with the dialog, so «Back» can never lose it.
   */
  const [advanced, setAdvanced] = useState(false);

  if (advanced) {
    return (
      <Stack spacing={3}>
        <Button
          variant="text"
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={() => setAdvanced(false)}
          sx={{ alignSelf: 'flex-start', textTransform: 'none', color: 'primary.dark' }}
          data-testid={STRATEGY_LIST.wizardAdvancedBack}
        >
          Back to AI settings
        </Button>

        <Typography variant="body2" color="text.secondary">
          Advanced settings apply to every block of this strategy. Leave the defaults unless a
          generation was cut short.
        </Typography>

        <TextField
          label="Max output tokens"
          type="number"
          size="small"
          value={maxTokens}
          onChange={(event) => onMaxTokensChange(sanitizeMaxOutputTokens(event.target.value))}
          onBlur={() => onMaxTokensChange(clampMaxOutputTokens(maxTokens))}
          inputProps={MAX_OUTPUT_TOKEN_BOUNDS}
          helperText={`Upper bound per block generation · the model default is ${DEFAULT_MAX_TOKENS_LABEL}.`}
          fullWidth
          disabled={disabled}
          data-testid={STRATEGY_LIST.formAIMaxTokens}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <AvailableModelSelector
          projectId={projectId}
          value={model}
          onChange={onModelChange}
          label="AI model"
          testId={STRATEGY_LIST.formAIModel}
          disabled={disabled || modelLoading}
          sx={{ width: '100%' }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {priceLine(pricing)}
        </Typography>
      </Box>

      {/* SG-A (T7b): the strategy-owned dispatch mode. Persisted with the
          strategy in the SAME write as the rest of the head, so the executor
          resolves ONE source of truth. Design: `.design/strategy-generation-mode/`. */}
      <BlockGenerationModeSelector
        value={blockGenerationMode}
        onChange={onBlockGenerationModeChange}
        disabled={disabled}
      />

      {/* W13 IS2: what the prompt carries about the pinned analysis — the
          theses are chosen on the «Insights» step, and this switch decides
          whether the actors' own words travel with them. */}
      <Box data-testid={STRATEGY_LIST.wizardInsightQuotes}>
        <FormControlLabel
          control={(
            <Switch
              checked={includeInsightSources}
              onChange={(event) => onIncludeInsightSourcesChange(event.target.checked)}
              size="small"
              disabled={disabled}
            />
          )}
          label="Include actor quotes"
          slotProps={{ typography: { variant: 'body2' } }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 6 }}>
          Sends the actors’ own words (up to 3 per thesis) under each selected thesis; larger
          prompts, and the AI may echo the wording.
        </Typography>
      </Box>

      <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
        <Button
          variant="text"
          size="small"
          onClick={() => setAdvanced(true)}
          disabled={disabled}
          sx={{ textTransform: 'none', color: 'primary.dark' }}
          data-testid={STRATEGY_LIST.wizardAdvancedSettings}
        >
          Advanced settings
        </Button>
        <Typography variant="caption" color="text.secondary">
          {`Max output tokens · default ${DEFAULT_MAX_TOKENS_LABEL}`}
        </Typography>
      </Stack>
    </Stack>
  );
};

AiSettingsStep.displayName = 'AiSettingsStep';
